import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { useLocation } from "react-router-dom";

import { AgentPanel } from "../agent/AgentPanel";
import { useTurnsFor } from "../agent/agentStore";
import type { MeView } from "../api/types";
import { Button } from "../ui/Button";
import { shellAgentTarget, type ShellAgentTarget } from "./agentTarget";

/*
  The agent panel outside the session (CP-27): a button in the top bar, and a panel that docks beside the page when it is
  open. The panel is closed to begin with, so a screen does not load a conversation nobody asked for. Whether it is open is
  kept in this browser, as a convenience only (storage can be blocked).
*/

const STORAGE_KEY = "enex.agent-panel";
export const SHELL_AGENT_ID = "shell-agent";

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "open";
  } catch {
    return false;
  }
}

function saveOpen(open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "open" : "closed");
  } catch {
    // The choice then lasts until the page is closed.
  }
}

export interface ShellAgent {
  /** Whom the panel talks to on this screen; null when the screen has a panel of its own or the person has no one to ask. */
  target: ShellAgentTarget | null;
  open: boolean;
  /** Counts up each time the person opens the panel, so the panel can take the focus once for each press. */
  focusRequest: number;
  launcherRef: RefObject<HTMLButtonElement | null>;
  toggle: () => void;
  close: (restoreFocus?: boolean) => void;
}

/** The state of the shell's agent panel: whom it talks to, whether it is open, and where the focus goes. */
export function useShellAgent(me: MeView): ShellAgent {
  const { pathname } = useLocation();
  const target = shellAgentTarget(me, pathname);
  const [wanted, setWanted] = useState(readOpen);
  const [focusRequest, setFocusRequest] = useState(0);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const open = wanted && target !== null;

  const set = useCallback((value: boolean) => {
    setWanted(value);
    saveOpen(value);
  }, []);

  const toggle = useCallback(() => {
    if (open) set(false);
    else {
      set(true);
      setFocusRequest((n) => n + 1);
    }
  }, [open, set]);

  const close = useCallback(
    (restoreFocus = false) => {
      set(false);
      if (restoreFocus) launcherRef.current?.focus();
    },
    [set],
  );

  return { target, open, focusRequest, launcherRef, toggle, close };
}

/** The button in the top bar that opens and closes the panel. It says when the agent is answering while the panel is closed. */
export function AgentLauncher({ agent }: { agent: ShellAgent }) {
  const { target, open, toggle, launcherRef } = agent;
  if (!target) return null;
  return <LauncherButton target={target} open={open} toggle={toggle} launcherRef={launcherRef} />;
}

function LauncherButton({ target, open, toggle, launcherRef }: { target: ShellAgentTarget; open: boolean; toggle: () => void; launcherRef: RefObject<HTMLButtonElement | null> }) {
  const answering = useTurnsFor(target.key).some((turn) => turn.status === "running");
  return (
    <button
      type="button"
      ref={launcherRef}
      className="button button--secondary shell-agent__launcher"
      aria-expanded={open}
      aria-controls={open ? SHELL_AGENT_ID : undefined}
      onClick={toggle}
    >
      {target.launcher}
      {answering && !open && (
        <>
          <span className="shell-agent__dot" aria-hidden="true" />
          <span className="sr-only"> (the agent is answering)</span>
        </>
      )}
    </button>
  );
}

/** The panel itself, docked beside the page. Escape closes it and puts the focus back on its button. */
export function AgentDock({ agent }: { agent: ShellAgent }) {
  const { target, open, focusRequest, close } = agent;
  const dockRef = useRef<HTMLElement>(null);

  // Each time the person opens the panel, the focus goes to the box to write in (or to the panel when the agent is off).
  useEffect(() => {
    if (focusRequest === 0 || !open) return;
    const box = dockRef.current?.querySelector<HTMLTextAreaElement>(".agent-panel__input");
    (box && !box.disabled ? box : dockRef.current)?.focus();
  }, [focusRequest, open]);

  if (!target || !open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    close(true);
  };

  return (
    <aside id={SHELL_AGENT_ID} ref={dockRef} tabIndex={-1} className="shell-agent" aria-label="Agent helper" onKeyDown={onKeyDown}>
      <AgentPanel key={target.key} conversationKey={target.key} mode={target.mode} title={target.title} context={target.context} emptyHint={target.emptyHint} />
      <div className="shell-agent__foot row">
        <Button variant="quiet" onClick={() => close(true)}>
          Hide the panel
        </Button>
      </div>
    </aside>
  );
}
